import React from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import "./TermsConditions.css"; // reuse the same CSS file

const RefundPolicy = () => {
  return (
    <React.Fragment>
      <Header variant="light" />
      <div className="refund-policy">
        <Helmet>
          <title>Refund Policy - Panmitra Online Services</title>
          <meta
            name="description"
            content="Refund Policy for Panmitra's PAN card, recharge, bill payment, insurance, and API services. Learn about our refund process, timelines, and conditions."
          />
        </Helmet>

        <div className="body-overlay"></div>

        <main>
          {/*===== HERO AREA STARTS =======*/}
          <div
            className="about-header-area"
            style={{
              backgroundImage: "url(assets/img/bg/inner-header.png)",
              backgroundRepeat: "no-repeat",
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          >
            <div className="container">
              <div className="row">
                <div className="col-lg-7 m-auto">
                  <div className="about-inner-header heading9 text-center">
                    <h1>Refund Policy</h1>
                    <a href="/">
                      Home <i className="fa-solid fa-angle-right" />{" "}
                      <span>Refund Policy</span>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ===== Refund Policy Content inside a Card (same class as Terms) ===== */}
          <div className="container py-5">
            <div className="terms-card p-5 bg-white rounded shadow-sm">
              <h1 className="fw-bold text-center mb-4">Refund Policy</h1>
              <p className="text-center text-muted effective-date">
                Last Updated: 01.03.2026
              </p>

              <section className="mb-5">
                <h2>1. Introduction</h2>
                <p>
                  This Refund Policy ("Policy") is applicable to all services
                  provided by{" "}
                  <strong>PANMITRA ONLINE SERVICE PRIVATE LIMITED</strong>{" "}
                  (operating under the brand name <strong>Panmitra</strong>). By
                  using our platform and services, you agree to the terms of
                  this Policy. We are committed to ensuring a fair and
                  transparent refund process for all users, including agents,
                  distributors, and end‑customers.
                </p>
                <p>
                  Our registered office is located at: <br />
                  <strong>PANMITRA ONLINE SERVICE PRIVATE LIMITED</strong>
                  <br />
                  Belagavi, Karnataka - 590015, India.
                </p>
              </section>

              <section className="mb-5">
                <h2>2. Definitions</h2>
                <ul>
                  <li>
                    <strong>Transaction:</strong> Any financial activity
                    conducted through our platform, including but not limited to
                    PAN card applications, mobile/DTH recharge, utility bill
                    payments, motor insurance purchases, and API service usage.
                  </li>
                  <li>
                    <strong>Failed Transaction:</strong> A transaction where the
                    amount is debited from the user’s account but not credited
                    to the intended beneficiary or service provider.
                  </li>
                  <li>
                    <strong>Successful Transaction:</strong> A transaction that
                    has been completed and confirmed by the beneficiary bank,
                    operator, government portal, or service provider.
                  </li>
                  <li>
                    <strong>Refund:</strong> Reversal of the debited amount back
                    to the user’s original payment source or designated bank
                    account.
                  </li>
                </ul>
              </section>

              <section className="mb-5">
                <h2>3. Applicability of Refunds</h2>
                <p>
                  Refunds may be initiated only under the following
                  circumstances:
                </p>
                <ul>
                  <li>
                    <strong>Failed Transactions:</strong> If a transaction fails
                    due to technical errors, bank downtime, or any reason on the
                    part of the payment gateway or service provider, and the
                    amount has been debited from your account.
                  </li>
                  <li>
                    <strong>Duplicate Transactions:</strong> If the same
                    transaction is processed more than once due to a system
                    error, resulting in multiple debits.
                  </li>
                  <li>
                    <strong>Technical Glitches:</strong> If our platform or a
                    partner service encounters a technical error that prevents
                    successful completion of a verified transaction.
                  </li>
                  <li>
                    <strong>Wrongly Deducted Amount:</strong> If an incorrect
                    amount is deducted from your account for a transaction.
                  </li>
                </ul>
              </section>

              <section className="mb-5">
                <h2>4. Non‑Applicability of Refunds</h2>
                <p>Refunds will not be provided in the following cases:</p>
                <ul>
                  <li>
                    <strong>Successful Transactions:</strong> Once a transaction
                    is successfully completed and the service (e.g., recharge,
                    bill payment, PAN application) is delivered, no refund shall
                    be issued.
                  </li>
                  <li>
                    <strong>User Error:</strong> If you provide incorrect
                    beneficiary details (wrong mobile number, wrong biller,
                    incorrect PAN applicant information, etc.) and the
                    transaction is processed successfully, no refund will be
                    provided.
                  </li>
                  <li>
                    <strong>Delayed Transactions:</strong> Transactions that are
                    delayed due to bank, operator, or government portal
                    processing times but are eventually successful.
                  </li>
                  <li>
                    <strong>Change of Mind:</strong> We do not offer refunds for
                    change of mind after a transaction has been initiated.
                  </li>
                  <li>
                    <strong>Disputes with Third Parties:</strong> Issues arising
                    from services provided by third parties (e.g., telecom
                    operators, electricity boards, insurance companies,
                    government agencies) are subject to their respective
                    policies and not covered by this refund policy.
                  </li>
                  <li>
                    <strong>PAN Application Rejection:</strong> If a PAN
                    application is rejected by the government authority due to
                    incorrect information provided by you, the government fees
                    are non‑refundable. Our service charges may also not be
                    refunded, except in case of a proven technical error on our
                    part.
                  </li>
                </ul>
              </section>

              <section className="mb-5">
                <h2>5. Refund Process and Timelines</h2>
                <p>
                  If you believe you are eligible for a refund, please follow
                  these steps:
                </p>
                <ol>
                  <li>
                    Contact our customer support at{" "}
                    <strong>panmitra121@gmail.com</strong> or call us at{" "}
                    <strong>+91 9632-456-111</strong> within 7 days of the
                    transaction.
                  </li>
                  <li>
                    Provide your transaction ID, mobile number, registered
                    email, and a brief description of the issue.
                  </li>
                  <li>
                    Our team will verify the transaction and, if eligible,
                    initiate the refund process.
                  </li>
                </ol>
                <p>
                  Once a refund is approved, the amount will be credited back to
                  the original payment source (bank account, card, wallet, etc.)
                  within <strong>5–7 business days</strong>. In some cases,
                  depending on your bank or payment gateway, it may take up to
                  10 business days.
                </p>
              </section>

              <section className="mb-5">
                <h2>6. Refund for Specific Services</h2>
                <ul>
                  <li>
                    <strong>PAN Card Services:</strong> If your PAN application
                    fails due to a technical error on our side and the
                    government fee is deducted, we will refund the government
                    fee as well as our service charges. However, if the
                    application is rejected by the government due to incorrect
                    data provided by you, no refund shall be applicable.
                  </li>
                  <li>
                    <strong>Mobile & DTH Recharge:</strong> For failed recharges
                    where the amount is debited, the refund will be processed
                    automatically within 48 hours. If not received within 7
                    days, please contact support.
                  </li>
                  <li>
                    <strong>Utility Bill Payments:</strong> If a bill payment is
                    deducted but not updated by the utility provider, we will
                    coordinate with the provider. If the transaction is
                    confirmed as failed, a refund will be initiated.
                  </li>
                  <li>
                    <strong>Motor Insurance:</strong> Insurance policies, once
                    issued by the insurer, are non‑refundable. If a transaction
                    fails and no policy is issued, a refund will be processed.
                  </li>
                  <li>
                    <strong>API Services:</strong> Charges for API access or
                    usage are non‑refundable unless the service is unavailable
                    due to our fault for an extended period, as determined by us
                    in our sole discretion.
                  </li>
                </ul>
              </section>

              <section className="mb-5">
                <h2>7. Dispute Resolution</h2>
                <p>
                  If you are not satisfied with the resolution provided by our
                  support team, you may escalate the matter in writing to our
                  Grievance Officer:
                </p>
                <address>
                  <strong>Grievance Officer</strong>
                  <br />
                  PANMITRA ONLINE SERVICE PRIVATE LIMITED
                  <br />
                  Belagavi, Karnataka - 590015, India
                  <br />
                  Email: panmitra121@gmail.com
                </address>
                <p>
                  We will acknowledge your complaint within 48 hours and strive
                  to resolve it within 30 days, as per applicable laws.
                </p>
              </section>

              <section className="mb-5">
                <h2>8. Contact Information</h2>
                <p>
                  For any queries related to refunds, please reach out to us:
                </p>
                <address>
                  <strong>PANMITRA ONLINE SERVICE PRIVATE LIMITED</strong>
                  <br />
                  Belagavi, Karnataka - 590015, India
                  <br />
                  Phone: +91 9632-456-111
                  <br />
                  Email: panmitra121@gmail.com
                  <br />
                  Web: https://instantpanmitra.com
                </address>
              </section>

              <section className="mb-5">
                <h2>9. Amendments</h2>
                <p>
                  We reserve the right to update or modify this Refund Policy at
                  any time without prior notice. Any changes will be effective
                  immediately upon posting on this page. We encourage you to
                  review this page periodically to stay informed about our
                  refund practices.
                </p>
              </section>

              <div className="text-center mt-5">
                <p className="mt-3 text-muted">
                  By using our services, you acknowledge that you have read and
                  understood this Refund Policy.
                </p>
              </div>
            </div>
          </div>
        </main>
      </div>
      <Footer />
    </React.Fragment>
  );
};

export default RefundPolicy;
